/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import {
  Typography,
  useTheme,
  Stack,
  Button,
  Box,
  LinearProgress,
} from "@mui/material";
import { PublishIcon, TrashIcon, DownloadIcon } from "~components/Icons";
import {
  InputFile,
  Headline,
  SubTitle,
  CustomModal,
  OverlayDragAndDrop,
  BrandSelector,
} from "~components/atoms";
import { ProductsGrid } from "~components/molecules";
import { CategoryTabs, ProductSearch } from "~components/Organisms";
import { useStreamContext } from "~contexts/Stream";
import { useSocketContext } from "~contexts/Socket";
import { useProductsContext } from "~contexts/Products";
import { useToastNotificationContext } from "~contexts/ToastNotification";
import { saveProductsCache } from "~api/stream";
import { authFetch } from "~helpers/authFetch";
import { STREAMS } from "~helpers/constants";
import { getStyles as getModalStyles } from "~components/atoms/CustomModal/styles";
import useCronJob from "../../../hooks/useCronJob";

const toastMessages = {
  success: {
    title: "informações salvas",
    text: "Atualização das categorias realizada com sucesso",
    cron: {
      title: "informações salvas",
      text: "Atualização da planilha realizada com sucesso",
    },
  },
  error: {
    title: "falha na requisição",
    text: "Problemas na requisição para atualizar categorias",
  },
};

const toastMessagesCron = {
  success: {
    title: "Atualização",
    text: "Atualização da planilha realizada com sucesso",
  },
  error: {
    title: "falha na requisição",
    text: "Problemas na  atualização da planilha ",
  },
};

const ProductsPage = () => {
  const theme: any = useTheme();
  const { modal: modalStyles } = getModalStyles(theme);
  const [isModalOpened, setModalOpened] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isFileOnDropzone, setFileOnDropZone] = useState<boolean>(false);
  const [preloadErrors, setPreloadErrors] = useState<
    { categoryId: number; attempts: number; message: string }[]
  >([]);
  const { setToastData } = useToastNotificationContext();
  const { loading, stream, currentStream } = useStreamContext();
  const { updateStreamSocket } = useSocketContext();
  const { refreshCronJobs, responseApi } = useCronJob();

  const {
    buttons,
    setButtons,
    buttonActive,
    productList,
    setProductList,
    deleteButton,
    setDroppedFile,
    loadCategoryProducts,
    loadingProducts,
    clearAllCache,
    selectCategoryFromCache,
    notFoundInVTEX,
    notFoundProductIds,
    loadedProductsFromSheet,
    getCategoryProductsFromCache,
    getCategoryNotFoundProductIds,
    getCurrentLoadingSessionId,
  } = useProductsContext();

  const [isNotFoundModalOpened, setNotFoundModalOpened] = useState<boolean>(false);
  const activeWorkersRef = useRef<Set<{ stop: boolean }>>(new Set());
  const savedCacheRef = useRef<string | null>(null);

  useEffect(() => {
    if (responseApi === "refresh") {
      if (currentStream) {
        updateStreamSocket(currentStream as any);
      }
      setToastData(toastMessagesCron.success, "success");

      setTimeout(() => {
        window.location.reload();
      }, 3000);
    }
  }, [responseApi, setToastData]);

  useEffect(() => {
    // Quando currentStream muda, sempre limpar produtos e botões anteriores primeiro
    if (currentStream) {
      const streamId = currentStream.id;
      const currentButtonsId = buttons.length > 0 ? buttons[0]?.id_stream : null;

      // Se mudou de stream, limpar tudo imediatamente
      if (currentButtonsId !== streamId && currentButtonsId !== null) {
        // Parar todos os workers ativos
        activeWorkersRef.current.forEach((worker) => {
          worker.stop = true;
        });
        activeWorkersRef.current.clear();

        clearAllCache();
        setProductList([]);
        setButtons([]);
        setPreloadErrors([]);
      }
    } else {
      // Se não há currentStream, limpar tudo
      // Parar todos os workers ativos
      activeWorkersRef.current.forEach((worker) => {
        worker.stop = true;
      });
      activeWorkersRef.current.clear();

      clearAllCache();
      setProductList([]);
      setButtons([]);
      setPreloadErrors([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStream?.id]);

  useEffect(() => {
    if (!currentStream) {
      savedCacheRef.current = null;
      return;
    }
    if (savedCacheRef.current && savedCacheRef.current !== currentStream.name) {
      savedCacheRef.current = null;
    }
  }, [currentStream?.name]);

  useEffect(() => {
    if (!loading && stream && currentStream) {
      const streamId = currentStream.id;
      const currentButtonsId = buttons.length > 0 ? buttons[0]?.id_stream : null;
      const isSameInitializedStream = currentButtonsId === streamId && buttons.length > 0;

      // Se já temos a stream atual inicializada no contexto, preservar carregamento/caches em andamento.
      if (isSameInitializedStream) {
        return;
      }

      // PARAR TODOS OS WORKERS ANTES DE INICIAR NOVOS
      // Isso garante que não há workers de streams anteriores rodando
      activeWorkersRef.current.forEach((worker) => {
        worker.stop = true;
      });
      activeWorkersRef.current.clear();

      // Limpar cache apenas quando de fato estamos inicializando nova stream.
      clearAllCache();

      // Limpar lista de produtos imediatamente para mostrar loading
      setProductList([]);

      // Verificar se o stream tem botões (categorias)
      if (stream.buttons && stream.buttons.length > 0) {
        // Apenas criar os botões (categorias), sem produtos
        const filteredButtons = stream.buttons.map((button) => ({
          ...button,
          products: [], // Produtos serão carregados sob demanda e em background
        }));

        setButtons(filteredButtons);
        setPreloadErrors([]);

        // Carregar produtos da primeira categoria para UI
        if (filteredButtons.length > 0 && filteredButtons[0].id_button) {
          loadCategoryProducts(filteredButtons[0].id_button, 1, false, true).catch(
            (error) => {
              console.error("Erro ao carregar primeira categoria:", error);
            }
          );

          // Pré-carregar as demais categorias em background com concorrência controlada
          const otherButtons = filteredButtons.slice(1).filter((b) => b.id_button);
          const categoryQueue = otherButtons.map((b) => b.id_button as number);
          const retryCounts: Record<number, number> = {};
          const MAX_CONCURRENT_CATEGORY_LOADS = 2;
          const MAX_RETRIES_PER_CATEGORY = 3;
          const RETRY_DELAY_MS = 30000;

          // Capturar sessionId no início - se mudar, significa que o stream mudou
          const initialSessionId = getCurrentLoadingSessionId();

          const worker = async () => {
            // Criar objeto de controle para este worker
            const workerControl = { stop: false };
            activeWorkersRef.current.add(workerControl);

            try {
              while (categoryQueue.length > 0 && !workerControl.stop) {
                // Verificar se o sessionId mudou (stream mudou) antes de processar próxima categoria
                const currentSessionId = getCurrentLoadingSessionId();
                if (currentSessionId !== initialSessionId || workerControl.stop) {
                  // Stream mudou ou worker foi marcado para parar, interromper imediatamente
                  workerControl.stop = true;
                  break;
                }

                const categoryId = categoryQueue.shift();
                if (!categoryId) continue;

                try {
                  // Verificar novamente antes de fazer a requisição
                  if (
                    getCurrentLoadingSessionId() !== initialSessionId ||
                    workerControl.stop
                  ) {
                    break;
                  }

                  await loadCategoryProducts(categoryId, 1, false, false);

                  // Verificar novamente após a requisição
                  if (
                    getCurrentLoadingSessionId() !== initialSessionId ||
                    workerControl.stop
                  ) {
                    break;
                  }
                } catch (error: any) {
                  // Se foi cancelado por mudança de stream, não tentar novamente
                  if (
                    error?.message === "Request cancelled - stream changed" ||
                    error?.message === "Request cancelled" ||
                    workerControl.stop ||
                    getCurrentLoadingSessionId() !== initialSessionId
                  ) {
                    break;
                  }

                  const current = retryCounts[categoryId] ?? 0;
                  if (
                    current < MAX_RETRIES_PER_CATEGORY &&
                    !workerControl.stop &&
                    getCurrentLoadingSessionId() === initialSessionId
                  ) {
                    retryCounts[categoryId] = current + 1;
                    categoryQueue.push(categoryId);
                    // pequeno delay para não pressionar a VTEX
                    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));

                    // Verificar novamente após o delay
                    if (
                      getCurrentLoadingSessionId() !== initialSessionId ||
                      workerControl.stop
                    ) {
                      break;
                    }
                  } else {
                    // Só adicionar erro se ainda estamos na mesma sessão
                    if (getCurrentLoadingSessionId() === initialSessionId) {
                      setPreloadErrors((prev) => {
                        if (prev.find((e) => e.categoryId === categoryId)) return prev;
                        return [
                          ...prev,
                          {
                            categoryId,
                            attempts: current + 1,
                            message:
                              error?.message ||
                              "Erro ao carregar produtos desta categoria. Tente novamente mais tarde.",
                          },
                        ];
                      });
                    }
                  }
                }
              }
            } finally {
              // Remover worker do conjunto quando terminar
              activeWorkersRef.current.delete(workerControl);
            }
          };

          const workerCount = Math.min(
            MAX_CONCURRENT_CATEGORY_LOADS,
            categoryQueue.length
          );
          for (let i = 0; i < workerCount; i++) {
            worker();
          }
        }
      }
    } else if (
      !loading &&
      currentStream &&
      (!stream || !stream.buttons || stream.buttons.length === 0)
    ) {
      // Se o stream foi carregado mas não tem botões, garantir que está limpo
      setButtons([]);
      setProductList([]);
      setPreloadErrors([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream, loading, currentStream]);

  useEffect(() => {
    if (!currentStream || buttons.length === 0) return;
    const totalProducts = buttons.reduce((sum, btn) => sum + (btn.quantity || 0), 0);
    if (!totalProducts) return;

    const loaded = Math.min(loadedProductsFromSheet, totalProducts);
    const isCompleted = loaded >= totalProducts && !loadingProducts;
    if (!isCompleted || preloadErrors.length > 0) return;

    const signatureParts = buttons
      .map((button) => {
        const id = button.id_button || 0;
        const cachedCount = id ? getCategoryProductsFromCache(id).length : 0;
        const quantity = button.quantity || 0;
        return `${id}:${quantity}:${cachedCount}`;
      })
      .join("|");
    const cacheSignature = `${currentStream.name}:${signatureParts}`;
    if (savedCacheRef.current === cacheSignature) return;

    const buttonsWithProducts = buttons.map((button) => ({
      ...button,
      products: button.id_button ? getCategoryProductsFromCache(button.id_button) : [],
    }));

    saveProductsCache(currentStream.name, { buttons: buttonsWithProducts })
      .then(() => {
        savedCacheRef.current = cacheSignature;
        // Emit socket event for real-time updates via stream refresh
        // Only emit if we have confirmed the cache is saved
        if (currentStream) {
          updateStreamSocket(currentStream as any);
        }
      })
      .catch((error) => {
        console.error("Error saving products cache:", error);
      });
  }, [
    currentStream,
    buttons,
    loadedProductsFromSheet,
    loadingProducts,
    preloadErrors.length,
    getCategoryProductsFromCache,
  ]);

  useEffect(() => {
    // Ao trocar de categoria, usar cache local já carregado nesta sessão.
    if (buttons.length > 0 && buttons[buttonActive]?.id_button && currentStream) {
      const categoryId = buttons[buttonActive].id_button;
      selectCategoryFromCache(categoryId);
    } else if (buttons.length === 0) {
      setProductList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonActive, currentStream]);

  // Se não há brand selecionada, mostrar mensagem (depois de todos os hooks)
  if (!currentStream) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          minHeight: "60vh",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            color: theme.palette.grey[0],
          }}
          variant="h1"
        >
          bem-vindo ao ambiente AzzasLive
        </Typography>
        <Typography
          sx={{
            color: theme.palette.grey[600],
            fontSize: "15px",
            marginBottom: "24px",
          }}
        >
          comece a configurar sua live pelo menu ao lado após selecionar uma marca
        </Typography>
        <BrandSelector invertColors={true} />
      </Box>
    );
  }

  const onExportSheet = () => {
    if (!buttons.length) return;

    const csvRows: string[] = [];
    // Cabeçalho
    csvRows.push(["ID", "Categoria", "Encontrado na VTEX"].join(";"));

    buttons.forEach((button) => {
      const categoryId = button.id_button;
      const categoryLabel = button.label || "";
      if (!categoryId) return;

      const productsFromCache = getCategoryProductsFromCache(categoryId) || [];
      const notFoundIds = getCategoryNotFoundProductIds(categoryId) || [];

      // Produtos encontrados na VTEX
      productsFromCache.forEach((product) => {
        const id = String(product.provider?.productId ?? product.id ?? "").trim();
        if (!id) return;
        csvRows.push([id, categoryLabel, "sim"].join(";"));
      });

      // Produtos não encontrados na VTEX
      notFoundIds.forEach((id) => {
        const trimmedId = String(id || "").trim();
        if (!trimmedId) return;
        csvRows.push([trimmedId, categoryLabel, "não"].join(";"));
      });
    });

    if (csvRows.length <= 1) return; // só cabeçalho, nada pra exportar

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    const filename = currentStream
      ? `produtos-${currentStream.name}.csv`
      : "produtos.csv";
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const SECONDARY_BUTTON = {
    icon: <PublishIcon />,
    text: "publicar",
    onAction: () => setModalOpened(true),
  };

  const PRIMARY_BUTTON = {
    icon: <DownloadIcon />,
    text: "exportar planilha",
    onAction: onExportSheet,
  };

  const onDeleteCategory = async () => {
    const deleted = await deleteButton();
    if (!deleted) {
      setToastData(toastMessages.error, "error");
      return;
    }

    if (currentStream) {
      updateStreamSocket({ categoriesUpdated: true, productsUpdated: true });
    }
  };

  const onPublish = async () => {
    const streamId = STREAMS.find((stream) => stream.name === currentStream.name)?.id;
    setLoading(true);
    await authFetch({
      path: `${process.env.REACT_APP_BACKEND_URL}/stream/${streamId}/categories`,
      method: "PUT",
      data: buttons,
    })
      .then((res) => {
        setToastData(toastMessages.success, "success");
        setModalOpened(false);
        return res.json();
      })
      .catch(() => setToastData(toastMessages.error, "error"))
      .finally(() => {
        setLoading(false);
      });
  };

  const onDragOverAndEnter = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    const isValidType =
      e.dataTransfer.items[0].type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      e.dataTransfer.items[0].type === "xlsx";

    if (isValidType) {
      setFileOnDropZone(true);
    }
  };

  const dropHandler = (ev: any) => {
    ev.preventDefault();
    ev.stopPropagation();
    setFileOnDropZone(false);

    if (ev.dataTransfer.items) {
      const isValidType =
        ev.dataTransfer.items[0].type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        ev.dataTransfer.items[0].type === "xlsx";
      if (ev.dataTransfer.items[0].kind === "file" && isValidType) {
        const file = ev.dataTransfer.items[0].getAsFile();
        setDroppedFile(file);
      }
    }
  };

  if (!stream || loading) return <>carregando...</>;

  const totalProducts = buttons.reduce((sum, btn) => sum + (btn.quantity || 0), 0);
  const isCatalogLoaded =
    !loadingProducts &&
    preloadErrors.length === 0 &&
    (totalProducts === 0 || loadedProductsFromSheet >= totalProducts);

  return (
    <div
      onDrop={dropHandler}
      onDragOver={onDragOverAndEnter}
      onDragLeave={() => setFileOnDropZone(false)}
    >
      <OverlayDragAndDrop isFileOnDropzone={isFileOnDropzone} />
      <Stack>
        <CustomModal isOpened={isModalOpened} onClose={() => setModalOpened(false)}>
          <Typography style={modalStyles.titleStyle}>
            confirmação de publicação
          </Typography>
          <Typography
            style={{
              ...modalStyles.textStyle,
              color: theme.palette.grey[400],
            }}
          >
            você solicitou publicar as modificações, deseja realmente publicar?
          </Typography>
          <div style={modalStyles.buttonsStyle}>
            <Button
              disableFocusRipple
              disableRipple
              onClick={() => setModalOpened(false)}
              sx={modalStyles.buttonDownloadStyle}
            >
              não, quero cancelar
            </Button>
            <Button
              disableFocusRipple
              disableRipple
              onClick={() => onPublish()}
              style={modalStyles.buttonCancelStyle}
            >
              {isLoading ? "publicando..." : "sim, quero publicar"}
            </Button>
          </div>
        </CustomModal>

        {/* Modal de produtos não encontrados na VTEX */}
        <CustomModal
          isOpened={isNotFoundModalOpened}
          onClose={() => setNotFoundModalOpened(false)}
        >
          <Typography style={modalStyles.titleStyle}>
            produtos não encontrados na VTEX
          </Typography>
          <Typography
            style={{
              ...modalStyles.textStyle,
              color: theme.palette.grey[400],
              marginBottom: "16px",
            }}
          >
            {notFoundInVTEX} produto(s) desta página não foram encontrados na VTEX:
          </Typography>
          <Box
            sx={{
              maxHeight: "400px",
              overflowY: "auto",
              backgroundColor: theme.palette.grey[700],
              borderRadius: "4px",
              padding: "16px",
              marginBottom: "16px",
            }}
          >
            {notFoundProductIds.length > 0 ? (
              <Stack spacing={1}>
                {notFoundProductIds.map((productId, index) => (
                  <Typography
                    key={index}
                    sx={{
                      fontSize: "14px",
                      color: theme.palette.grey[300],
                      fontFamily: "monospace",
                    }}
                  >
                    {productId}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{
                  fontSize: "14px",
                  color: theme.palette.grey[400],
                }}
              >
                Nenhum ID disponível
              </Typography>
            )}
          </Box>
          <div style={modalStyles.buttonsStyle}>
            <Button
              disableFocusRipple
              disableRipple
              onClick={() => setNotFoundModalOpened(false)}
              style={{
                ...modalStyles.buttonCancelStyle,
                backgroundColor: theme.palette.grey[700],
                width: "75px",
                borderRadius: "100px",
                marginLeft: "0px",
              }}
            >
              fechar
            </Button>
          </div>
        </CustomModal>

        <Headline
          id="products"
          title="produtos"
          primaryButton={PRIMARY_BUTTON}
          secondaryButton={SECONDARY_BUTTON}
          description="não esqueça de salvar as alterações antes de sair"
        />
        <SubTitle
          title="lista de produtos"
          description="busque e adicione produtos manualmente ou faça o upload de uma planilha excel"
        />
        <Typography style={{ fontSize: "12px", color: "#858585" }}>
          {" "}
          para baixar uma planilha padrão{" "}
          <a
            style={{ color: "#858585" }}
            href="https://storage.googleapis.com/somalive-public/production/planilhas-cadastro-produtos/farm-aovivo-resultado-cadastro.xlsx"
          >
            clique aqui
          </a>
        </Typography>

        <InputFile />

        <Stack
          direction="row"
          alignItems="center"
          sx={{ mb: "68px", mt: "-40px" }}
          spacing={2}
        >
          <Button onClick={refreshCronJobs} sx={{ p: 0, mr: "5px" }}>
            reenviar planilha
          </Button>
          <img
            alt="reenvio"
            src="https://storage.googleapis.com/somalive-assets/reenvio.svg"
            height={24}
            width={24}
          />
        </Stack>

        {/* Barra de progresso do carregamento de todos os produtos da planilha */}
        {buttons.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {(() => {
              const totalProducts = buttons.reduce(
                (sum, btn) => sum + (btn.quantity || 0),
                0
              );
              if (!totalProducts) return null;

              const loaded = Math.min(loadedProductsFromSheet, totalProducts);
              const progress = (loaded / totalProducts) * 100;
              const isCompleted = loaded >= totalProducts && !loadingProducts;

              return (
                <>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: theme.palette.grey[400],
                      mb: 0.5,
                    }}
                  >
                    {isCompleted
                      ? `carregamento concluído (${totalProducts} items)`
                      : `carregando produtos da planilha (${loaded} de ${totalProducts})`}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Number.isFinite(progress) ? Math.min(progress, 100) : 0}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: isCompleted
                        ? theme.palette.success.light
                        : theme.palette.grey[700],
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: isCompleted
                          ? theme.palette.success.main
                          : theme.palette.warning.main,
                      },
                    }}
                  />
                </>
              );
            })()}
          </Box>
        )}

        {/* Aviso de erro de pré-carregamento de categorias */}
        {preloadErrors.length > 0 && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: theme.palette.error.dark,
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                color: theme.palette.common.white,
                fontWeight: 500,
              }}
            >
              algumas categorias não puderam ser carregadas automaticamente. você ainda
              pode navegar normalmente, mas alguns produtos podem não aparecer. tente
              atualizar a página.
            </Typography>
          </Box>
        )}

        <CategoryTabs />
        <ProductSearch disabled={!isCatalogLoaded} />

        <Stack
          justifyContent="space-between"
          alignItems="center"
          direction="row"
          sx={{ mt: 3 }}
        >
          <Stack direction="column" spacing={0.5}>
            <Typography>
              {buttons[buttonActive]?.quantity !== undefined
                ? `${productList.length} produtos nesta categoria ( de ${buttons[buttonActive].quantity} )`
                : `${productList.length} produtos nesta categoria`}
            </Typography>
            <Box sx={{ minHeight: "20px", display: "flex", alignItems: "center" }}>
              {notFoundInVTEX > 0 && !loadingProducts && (
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: theme.palette.grey[400],
                    cursor: "pointer",
                    textDecoration: "underline",
                    "&:hover": {
                      color: theme.palette.grey[300],
                    },
                  }}
                  onClick={() => setNotFoundModalOpened(true)}
                >
                  {notFoundInVTEX} produtos desta página não encontrados na VTEX
                </Typography>
              )}
            </Box>
          </Stack>
          <Stack direction="column" spacing={0} alignItems="flex-end">
            <Button
              variant="text"
              disableRipple
              disableFocusRipple
              onClick={onDeleteCategory}
              endIcon={<TrashIcon sx={{ color: "transparent" }} />}
              sx={{ mt: 0 }}
            >
              excluir categoria
            </Button>
          </Stack>
        </Stack>

        <ProductsGrid products={productList} page="products" canEdit={isCatalogLoaded} />
      </Stack>
    </div>
  );
};

export default ProductsPage;
